import { useNavigate } from "react-router";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import {
  TabbedDrawer,
  TabbedDrawerHeader,
  TabbedDrawerTitle,
  TabbedDrawerNavigation,
  TabbedDrawerTab,
} from "@sellar-io/components";
import {
  OrderCommentList,
  OrderSummaryContainer,
  ProfilePicture,
  SpinnerLegacy,
} from "../../components";
import {
  calculateItemTotal,
  calculateVatTotal,
  formatCurrency,
} from "@sellar-io/common";
import { renderTaxName } from "../../utils/sellarHelpers";
import { ORDER_DETAILS } from "../../constants/routes";
import { useOrder } from "../../hooks/api/v2/order/useOrder";
import { useSupplierRelationship } from "../../hooks/api/v2/supplierRelationship/useSupplierRelationship";
import { SharedOrderCards } from "../order/SharedOrderCards";

enum CollectionOrderDrawerTabs {
  Details,
  Comments,
}

const tabs = [
  { label: "Details", index: CollectionOrderDrawerTabs.Details },
  { label: "Comments", index: CollectionOrderDrawerTabs.Comments },
];

export const CollectionOrderDrawer = ({
  active,
  setActive,
  orderId,
}: {
  active: boolean;
  setActive: (value: boolean) => void;
  orderId?: number;
}) => {
  return (
    <TabbedDrawer
      active={active}
      setActive={setActive}
      initTab={CollectionOrderDrawerTabs.Details}
    >
      {active && orderId && <Content orderId={orderId} />}
    </TabbedDrawer>
  );
};

const Content = ({ orderId }: { orderId: number }) => {
  const navigate = useNavigate();
  const { data: order, isLoading } = useOrder(orderId);

  return (
    <>
      <TabbedDrawerHeader>
        <TabbedDrawerTitle
          title={`${order?.Retailer.Business.name} - ${formatCurrency(order?.price ?? 0)}`}
          subtitle={`Order #${order?.id}`}
          customIcon={
            <ProfilePicture
              logoUrl={order?.Retailer.Business.logoUrl}
              fallback="supplier-badge"
              size="large"
            />
          }
          onClick={
            order?.id
              ? () => navigate(ORDER_DETAILS.href({ orderId: order.id }))
              : undefined
          }
        />
        <TabbedDrawerNavigation tabs={tabs} />
      </TabbedDrawerHeader>

      <TabbedDrawerTab tab={CollectionOrderDrawerTabs.Details}>
        {order && <DetailsContent order={order} isLoading={isLoading} />}
      </TabbedDrawerTab>

      <TabbedDrawerTab tab={CollectionOrderDrawerTabs.Comments}>
        {orderId && <CommentsContent orderId={orderId} />}
      </TabbedDrawerTab>
    </>
  );
};

const DetailsContent = ({
  order,
  isLoading,
}: {
  order: OrderGetResponseType;
  isLoading: boolean;
}) => {
  const { data: connection } = useSupplierRelationship(order.id, {
    enabled: !!order.id,
  });

  if (isLoading) return <SpinnerLegacy />;

  if (!order) return;

  return (
    <div className="flex flex-col gap-2 overflow-hidden bg-zinc-100 p-4">
      <OrderSummaryContainer
        stripeInvoiceId={order.stripeInvoiceId}
        supplierName={order.Supplier.Business.name}
        confirmedAt={order.confirmedAt}
        checkoutVersion={order.checkoutVersion}
        cancelledAt={order.cancelledAt}
        paymentDue={order.paymentDue}
        paymentType={order.paymentType}
        items={order.OrderItems.map((orderItem) => ({
          description: orderItem.Product.name ?? "",
          quantity: orderItem.quantity,
          cost: formatCurrency(orderItem.price),
          sku: orderItem.Product.sku,
          fullPrice: orderItem.fullPrice,
          price: orderItem.price,
          ProductId: orderItem.ProductId,
          ParentId: orderItem.Product.ParentId,
          promotionAppliedAt: orderItem.promotionAppliedAt,
        }))}
        priceProps={{
          delivery: formatCurrency(order.deliveryCharge, order.currency),
          isDeliveryChargeCapped: order.deliveryChargeCapped,
          subTotal: order.OrderItems
            ? formatCurrency(
                calculateItemTotal(order.OrderItems),
                order.currency,
              )
            : "",
          discount:
            order.discountAmount > 0
              ? {
                  value: formatCurrency(order.discountAmount, order.currency),
                  calculatedTotal: formatCurrency(
                    order.discountAmount,
                    order.currency,
                  ),
                }
              : undefined,
          tax: {
            name: renderTaxName(order.currency),
            percentage: order.vatPercentage.toString(),
            total: formatCurrency(
              calculateVatTotal({ ...order, orderItems: order.OrderItems }),
              order.currency,
            ),
          },
          total: formatCurrency(order.price, order.currency),
        }}
      />
      <SharedOrderCards
        order={order}
        connection={connection}
        buyer={order.Retailer}
        showComments={false}
      />
    </div>
  );
};

const CommentsContent = ({ orderId }: { orderId: number }) => {
  const { data: connection } = useSupplierRelationship(orderId, {
    enabled: !!orderId,
  });

  return (
    <div className="flex h-full flex-col gap-2 overflow-hidden bg-zinc-100 p-4">
      <OrderCommentList orderId={orderId} connection={connection} />
    </div>
  );
};
